import api from './api';

/**
 * Global Inclusions API Service
 */

/**
 * Get paginated list of global inclusions
 * @param {Object} filters - Filter parameters
 * @param {number} page - Page number
 * @param {number} per_page - Items per page
 * @returns {Promise<Object>} Response with inclusions data and pagination info
 */
export const getGlobalInclusions = async (filters = {}, page = 1, per_page = 50) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });

    if (filters.search) params.append('search', filters.search);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/api/admin/package-lists/inclusions?${params.toString()}`);
    
    // The api.js interceptor already extracts the data array to response.data
    // and preserves meta info at response.meta for paginated responses
    return {
      data: response.data,  // The extracted array of inclusions
      meta: response.meta   // Pagination information
    };
  } catch (error) {
    console.error('❌ getGlobalInclusions error:', error);
    throw error;
  }
};

/**
 * Create new global inclusion
 * @param {Object} inclusionData - Inclusion data
 * @returns {Promise<Object>} Created inclusion
 */
export const createGlobalInclusion = async (inclusionData) => {
  try {
    const response = await api.post('/api/admin/package-lists/inclusions', inclusionData);
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ createGlobalInclusion error:', error);
    throw error;
  }
};

/**
 * Update global inclusion
 * @param {string} id - Inclusion ID
 * @param {Object} inclusionData - Updated inclusion data
 * @returns {Promise<Object>} Updated inclusion
 */
export const updateGlobalInclusion = async (id, inclusionData) => {
  try {
    const response = await api.put(`/api/admin/package-lists/inclusions/${id}`, inclusionData);
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ updateGlobalInclusion error:', error);
    throw error;
  }
};

/**
 * Delete global inclusion
 * @param {string} id - Inclusion ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteGlobalInclusion = async (id) => {
  try {
    const response = await api.delete(`/api/admin/package-lists/inclusions/${id}`);
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ deleteGlobalInclusion error:', error);
    throw error;
  }
};

/**
 * Update display order for multiple inclusions
 * @param {Array} orderData - Array of {id: string, display_order: number}
 * @returns {Promise<Object>} Update confirmation
 */
export const updateGlobalInclusionsOrder = async (orderData) => {
  try {
    const response = await api.put('/api/admin/package-lists/inclusions/bulk-order', {
      items: orderData
    });
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ updateGlobalInclusionsOrder error:', error);
    throw error;
  }
};

/**
 * Global Exclusions API Service
 */

/**
 * Get paginated list of global exclusions
 * @param {Object} filters - Filter parameters
 * @param {number} page - Page number
 * @param {number} per_page - Items per page
 * @returns {Promise<Object>} Response with exclusions data and pagination info
 */
export const getGlobalExclusions = async (filters = {}, page = 1, per_page = 50) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString()
    });

    if (filters.search) params.append('search', filters.search);
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/api/admin/package-lists/exclusions?${params.toString()}`);
    
    // The api.js interceptor already extracts the data array to response.data
    // and preserves meta info at response.meta for paginated responses
    return {
      data: response.data,  // The extracted array of exclusions
      meta: response.meta   // Pagination information
    };
  } catch (error) {
    console.error('❌ getGlobalExclusions error:', error);
    throw error;
  }
};

/**
 * Create new global exclusion
 * @param {Object} exclusionData - Exclusion data
 * @returns {Promise<Object>} Created exclusion
 */
export const createGlobalExclusion = async (exclusionData) => {
  try {
    const response = await api.post('/api/admin/package-lists/exclusions', exclusionData);
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ createGlobalExclusion error:', error);
    throw error;
  }
};

/**
 * Update global exclusion
 * @param {string} id - Exclusion ID
 * @param {Object} exclusionData - Updated exclusion data
 * @returns {Promise<Object>} Updated exclusion
 */
export const updateGlobalExclusion = async (id, exclusionData) => {
  try {
    const response = await api.put(`/api/admin/package-lists/exclusions/${id}`, exclusionData);
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ updateGlobalExclusion error:', error);
    throw error;
  }
};

/**
 * Delete global exclusion
 * @param {string} id - Exclusion ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteGlobalExclusion = async (id) => {
  try {
    const response = await api.delete(`/api/admin/package-lists/exclusions/${id}`);
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ deleteGlobalExclusion error:', error);
    throw error;
  }
};

/**
 * Update display order for multiple exclusions
 * @param {Array} orderData - Array of {id: string, display_order: number}
 * @returns {Promise<Object>} Update confirmation
 */
export const updateGlobalExclusionsOrder = async (orderData) => {
  try {
    const response = await api.put('/api/admin/package-lists/exclusions/bulk-order', {
      items: orderData
    });
    // For non-paginated responses, api.js extracts response.data.data to response.data
    return response.data;
  } catch (error) {
    console.error('❌ updateGlobalExclusionsOrder error:', error);
    throw error;
  }
};
